/**
 * Overlapping event stacking layout for calendar events.
 *
 * Produces ONE block per CalendarItem (no duplication for multi-assignee items).
 * Overlapping events use an N+1 virtual lane model where each event spans 2 lanes
 * and is shifted by 1 lane, creating intentional overlap for readability.
 *
 * @param {Array} items - CalendarItemWithDetails objects from GraphQL
 * @param {number} gridOriginMs - epoch ms of grid start (e.g. 6 AM of the day)
 * @param {number} hourHeight - pixels per hour
 * @param {number} minBlockHeight - minimum block height in pixels
 * @returns {Array} Layout blocks with positioning and color info
 */
export function computeLaneLayout(items, gridOriginMs, hourHeight, minBlockHeight) {
  if (!items || items.length === 0) return [];

  // 0. All-day (personal "SIX") items never become hour blocks. Hosts split them
  //    into AllDayEventStrip BEFORE calling this; the drop here is the
  //    belt-and-braces guard so an unsplit caller can't paint a noon-anchored
  //    block over the 12p row. [R2-G1]
  const timed = items.filter((item) => item?.calendarItem?.isAllDay !== true);
  if (timed.length === 0) return [];

  // 1. Build event objects with parsed times
  const events = timed.map((item) => {
    const ci = item.calendarItem;
    const startMs = parseInt(ci.startMs) || gridOriginMs;
    const endMs = parseInt(ci.endMs) || startMs + 3600000; // default 1 hour
    return { item, startMs, endMs, duration: endMs - startMs, id: ci._id || "" };
  });

  // 2. Sort: start ascending, duration descending (longest first), stable by id
  events.sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    if (a.duration !== b.duration) return b.duration - a.duration; // longest first
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  // 3. Build overlap clusters (sweep line)
  const clusters = [];
  let currentCluster = [];
  let clusterEnd = -Infinity;

  for (const event of events) {
    if (currentCluster.length > 0 && event.startMs >= clusterEnd) {
      clusters.push(currentCluster);
      currentCluster = [];
      clusterEnd = -Infinity;
    }
    currentCluster.push(event);
    clusterEnd = Math.max(clusterEnd, event.endMs);
  }
  if (currentCluster.length > 0) clusters.push(currentCluster);

  // 4. Assign lanes based on actual pairwise overlaps (not just cluster membership)
  const result = [];

  for (const cluster of clusters) {
    // Greedy lane assignment: each event gets the lowest lane not used by
    // events it actually overlaps with (not all events in the cluster).
    const laneAssignments = new Array(cluster.length);

    for (let i = 0; i < cluster.length; i++) {
      const event = cluster[i];
      const usedLanes = new Set();

      for (let j = 0; j < i; j++) {
        // Check actual pairwise overlap (strict: touching endpoints don't overlap)
        if (cluster[j].startMs < event.endMs && event.startMs < cluster[j].endMs) {
          usedLanes.add(laneAssignments[j]);
        }
      }

      let lane = 0;
      while (usedLanes.has(lane)) lane++;
      laneAssignments[i] = lane;
    }

    const maxLane = Math.max(...laneAssignments);
    // N+1 virtual lanes: each event spans 2 lanes, so max lane + 2
    const totalVirtualLanes = maxLane + 2;

    for (let i = 0; i < cluster.length; i++) {
      const event = cluster[i];

      const topMinutes = (event.startMs - gridOriginMs) / 60000;
      const durationMinutes = (event.endMs - event.startMs) / 60000;
      const topPx = (topMinutes / 60) * hourHeight;
      const heightPx = Math.max((durationMinutes / 60) * hourHeight, minBlockHeight) - 2;

      // Compute background color
      const ci = event.item.calendarItem;
      const userIds = ci.assignedUserIds || [];
      const users = event.item.assignedUsers || [];
      const eventType = ci.eventType || "job";

      // Tentative shimmer only applies to jobs (Go By and Vacation have their
      // own solid colors and are never "tentative" in the invoice sense).
      const isTentative = eventType === "job" && !ci.invoiceId;
      const isOrange = eventType === "job" && (!ci.invoiceId || userIds.length === 0);

      let background;
      if (eventType === "go_by") {
        background = "#009688"; // teal
      } else if (eventType === "vacation") {
        // Vacation now uses the assigned refinisher's color as the base.
        // The .vacation-stripes CSS class layers a slight darken overlay
        // and 45° red diagonal stripes ON TOP via a pseudo-element so the
        // refinisher's color still shows through. Gray fallback for the
        // edge case where a vacation event somehow has no assigned user
        // (validation prevents this on create, but render defensively).
        background = users[0]?.defaultColor || "#607D8B";
      } else if (eventType === "personal") {
        // Personal ("SIX") event: the CREATOR's colour. The server stamps
        // assignedUserIds to the creator, so assignedUsers[0] is the creator.
        background = personalEventColor(event.item);
      } else if (isOrange) {
        background = "#FF9800";
      } else if (users.length === 0) {
        background = "#9e9e9e";
      } else if (users.length === 1) {
        background = users[0]?.defaultColor || "#9e9e9e";
      } else {
        // Multi-user job (2026-05-20). Previous behavior was a
        // linear-gradient blending every assigned user's defaultColor.
        // Now: solid Flamingo (#E67C73, "salmon") so a multi-assignee
        // job reads as a distinct state at a glance. Only the job
        // branch lands here — go_by and vacation branched out above.
        background = "#E67C73";
      }

      result.push({
        item: event.item,
        topPx,
        heightPx,
        overlapIndex: laneAssignments[i],
        totalVirtualLanes,
        background,
        isCompleted: ci.itemStatus === "APPROVED",
        isPending: ci.itemStatus === "PENDING",
        isTentative,
        eventType,
        // Always false here (all-day items were dropped above); emitted so a
        // consumer can assert the invariant rather than re-derive it.
        isAllDay: ci.isAllDay === true,
        chainGroupId: ci.chainGroupId || null,
        // Chain-link icon eligibility: non-null group AND non-vacation.
        // Vacation is excluded from chain UI per §6.12 — defensive check here
        // also protects against legacy rogue data.
        isChained: !!ci.chainGroupId && eventType !== "vacation",
      });
    }
  }

  return result;
}

/**
 * Returns the prefix that comes before a customer/assigned name on a calendar
 * block label. Used by WeekModal / DayModal / DayViewModal so per-type prefix
 * logic lives in one place.
 *
 * - "go_by"   → "Go By — "
 * - "vacation" → "" (no prefix — vacation uses a name-suffix format
 *   "{Name} - Day Off" handled by getBlockLabelName)
 * - "job" with no invoice → "Tent. - "  (tentative, legacy behavior)
 * - "job" with invoice    → ""
 *
 * `calendarItem` must be the `.calendarItem` sub-object (not the wrapper with
 * customer + invoice).
 */
export function getBlockLabelPrefix(calendarItem) {
  const t = calendarItem?.eventType || "job";
  if (t === "go_by") return "Go By — ";
  if (t === "vacation") return "";
  // Personal ("SIX"): the label IS the title — no prefix, never "Tent.".
  if (t === "personal") return "";
  return !calendarItem?.invoiceId ? "Tent. - " : "";
}

/**
 * Personal ("SIX") event colour = the creator's defaultColor. One rule shared
 * by the lane block, the all-day chip and the detail popup header. Muted gray
 * fallback when the creator carries no colour.
 */
export function personalEventColor(item) {
  return item?.assignedUsers?.[0]?.defaultColor || "#607D8B";
}

/**
 * Returns the name portion of a calendar block label.
 *
 * - Vacation: the assigned refinisher's full name (from `item.assignedUsers[0]`),
 *   falling back to "Refinisher" if no assignee on the wrapper.
 * - Job / Go By: the customer's full name (from `item.customer`), falling
 *   back to empty string if no customer on the wrapper.
 *
 * `item` is the `CalendarItemWithDetails`-shaped wrapper (has `calendarItem`,
 * `customer`, `assignedUsers`, etc.).
 */
export function getBlockLabelName(item) {
  const ci = item?.calendarItem;
  const t = ci?.eventType || "job";
  // Personal ("SIX"): the required label is the whole name.
  if (t === "personal") return ci?.label || "";
  if (t === "vacation") {
    const u = item?.assignedUsers?.[0];
    let name = "Refinisher";
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      if (full) name = full;
    }
    return `${name} - Day Off`;
  }
  const c = item?.customer;
  if (c) {
    const full = `${c.firstName || ""} ${c.lastName || ""}`.trim();
    if (full) return full;
  }
  return "";
}
